import React, { useMemo } from 'react'
import { Button } from '@chakra-ui/react'
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react'

import {
    ClientInfo,
    PatientInfo,
    ClientFieldsFragment,
} from '~graphql/generated/graphql'
import {
    formatEmail,
    getBasePhoneNumber,
    getClientFullName,
} from '~utils/helpers'
import { formatPhoneReadable } from '~utils/phone'
import OutlineBox from '~components/shared/wrappers/outline-box'
import MatchingTag from '../matching-tag'
import { useClinic } from '~contexts/clinic-context'

interface Props {
    client: ClientFieldsFragment
    clientInfo: ClientInfo
    patientInfo: PatientInfo
    setClient: (client: ClientFieldsFragment) => void
}

export default function ClientItem({
    client,
    clientInfo,
    patientInfo,
    setClient,
}: Props) {
    const { clinic } = useClinic()
    const { first_name, last_name, email, phone, patients } = client

    const matchingFirstName =
        first_name?.toLowerCase() === clientInfo.first_name?.toLowerCase()
    const matchingLastName =
        last_name?.toLowerCase() === clientInfo.last_name?.toLowerCase()
    const matchingEmail =
        email?.toLowerCase() === clientInfo.email?.toLowerCase()
    const matchingPhone =
        getBasePhoneNumber(phone) === getBasePhoneNumber(clientInfo.phone)

    const matchingPatients = useMemo(
        () =>
            patients?.filter(
                p =>
                    p.name?.toLowerCase() ===
                    patientInfo?.patient_name?.toLowerCase(),
            ),
        [patients, patientInfo],
    )

    return (
        <OutlineBox w="full" py={3} px={5}>
            <Flex
                w="full"
                justifyContent="space-between"
                alignItems="center"
                gridGap={4}
            >
                <Box flex={1} overflow="hidden">
                    <Text fontWeight="semibold" mb={-0.5}>
                        {getClientFullName(client)}
                    </Text>
                    <Text
                        fontSize="xs"
                        color="gray.500"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        overflow="hidden"
                    >
                        {formatEmail(client.email)}
                    </Text>
                    <Text color="gray.500" fontSize="xs">
                        {formatPhoneReadable(
                            client.phone,
                            client?.address?.country ||
                                clinic?.address?.country,
                        )}
                    </Text>
                </Box>
                <SimpleGrid columns={2} spacing={2}>
                    <MatchingTag
                        doesMatch={[matchingFirstName, matchingLastName]}
                        attribute="Name"
                        matchingFields={['First Name', 'Last Name']}
                    />
                    <MatchingTag
                        doesMatch={[matchingEmail]}
                        attribute="Email"
                    />
                    <MatchingTag
                        doesMatch={[matchingPhone]}
                        attribute="Phone"
                    />
                    <MatchingTag
                        doesMatch={[
                            !!matchingPatients && matchingPatients?.length > 0,
                        ]}
                        attribute="Patient"
                    />
                </SimpleGrid>
                <Button
                    colorScheme="brand"
                    size="sm"
                    shadow="md"
                    onClick={() => setClient(client)}
                >
                    Select
                </Button>
            </Flex>
        </OutlineBox>
    )
}
